/* eslint-disable @typescript-eslint/no-misused-promises */
import { FormEvent, useEffect, useState } from "react";
import {
  confirmPasswordMatch,
  validateEmail,
  validatePassword,
} from "@/lib/validators";

import { Button } from "@/components/ui/button";
import { CardWrapper } from "@/components/card-wrapper";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInputComponent } from "@/components/ui/password-input";
import { RequestSource } from "@/lib/errorHandling";
import { SocialAuth } from "@/components/social-auth";
import { toast } from "sonner";
import { useHandleError } from "@/hooks/useHandleError";
import { useNavigate } from "react-router";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";

export function SignupPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { handleError } = useHandleError();
  const { register, login } = usePocketBase();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(
      email !== "" &&
        password !== "" &&
        confirmPassword !== "" &&
        emailError === "" &&
        passwordError === "" &&
        confirmPasswordError === "",
    );
  }, [
    email,
    password,
    confirmPassword,
    emailError,
    passwordError,
    confirmPasswordError,
  ]);

  const handleSignUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateEmail(email, setEmailError);
    validatePassword(password, setPasswordError);
    confirmPasswordMatch(password, confirmPassword, setConfirmPasswordError);
    if (!isFormValid) {
      return;
    }
    try {
      await register(email, password, confirmPassword);
      await login(email, password);
      // on account creation, user flags will be empty, so we can directly navigate to user info page without checking flags
      navigate("/app/user-info");
      // navigate("/email-confirmation", { state: { email } });
    } catch (error) {
      toast.error(handleError(RequestSource.SIGNUP, error));
    }
  };

  // const handleSocialSignUp = (provider: string) => {
  //   // Handle social sign up logic here
  //   console.log(`Sign up with ${provider}`);
  // };

  return (
    <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
      <CardWrapper className="w-[380px] md:w-[450px]">
        <div className="space-y-6 p-4 px-8">
          <div className="space-y-2 text-center">
            <h2 className="mt-6 text-3xl font-bold text-foreground">
              {t("pages:loginSignup.signupTitle")}
            </h2>
            <p className="hidden md:inline text-gray-500 dark:text-gray-400">
              {t("pages:loginSignup.signupSubtitle")}
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSignUp}>
            <div className="space-y-4 rounded-md shadow-sm">
              <div>
                <Label htmlFor="email-address">{t("common:email")}</Label>
                <Input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    validateEmail(e.target.value, setEmailError);
                  }}
                  className={emailError ? "border-red-500" : ""}
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
              </div>
              <div>
                <Label htmlFor="password">{t("common:password")}</Label>
                <PasswordInputComponent
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validatePassword(e.target.value, setPasswordError);
                  }}
                  className={passwordError ? "border-red-500" : ""}
                />
                {passwordError && (
                  <p className="text-sm text-red-500">{passwordError}</p>
                )}
              </div>
              <div>
                <Label htmlFor="confirm-password">
                  {t("common:passwordConfirmation")}
                </Label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    confirmPasswordMatch(
                      password,
                      e.target.value,
                      setConfirmPasswordError,
                    );
                  }}
                  className={confirmPasswordError ? "border-red-500" : ""}
                />
                {confirmPasswordError && (
                  <p className="text-sm text-red-500">{confirmPasswordError}</p>
                )}
              </div>
            </div>

            <div>
              <Button type="submit" className="w-full" disabled={!isFormValid}>
                {t("pages:loginSignup.signupButton")}
              </Button>
            </div>
          </form>

          {/* <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("pages:loginSignup.orContinueWith")}
              </span>
            </div>
          </div> */}
          {/* <SocialAuth handleSocialAuth={handleSocialSignUp} /> */}
        </div>
      </CardWrapper>
    </div>
  );
}
